import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HelpCircle, ChevronDown, ArrowLeft, Beaker } from 'lucide-react';
import { Button } from '@/components/ui/button';
import SEO from '@/components/SEO';

// FAQ Data
const FAQ_DATA = [
  {
    category: "General",
    questions: [
      {
        q: "What exactly is TabedLab?",
        a: "TabedLab is an intelligent platform that leverages AI to read, extract, and interpret laboratory spectral and chromatographic data from various files (CSV, Excel, PDF, Images). It transforms raw data into visual reports and actionable insights."
      },
      {
        q: "Do I need programming or data science skills?",
        a: "Not at all. TabedLab was designed specifically for chemists, researchers, and laboratory professionals. If you know how to upload a file, you know how to use the platform."
      }
    ]
  },
  {
    category: "Security & Privacy",
    questions: [
      {
        q: "Is my data secure?",
        a: "Yes. We use industry-standard encryption. Your files are processed on secure servers and are never shared with third parties."
      },
      {
        q: "Does the AI use my data to train other models?",
        a: "No. At TabedLab, your data remains private. It is used solely to generate your specific analysis and is not used for AI training purposes."
      }
    ]
  },
  {
    category: "Technical",
    questions: [
      {
        q: "Which file formats are supported?",
        a: "We accept CSV files, Excel spreadsheets (XLSX), digital or scanned PDFs, and images (JPG, PNG via OCR)."
      },
      {
        q: "Can the AI make mistakes?",
        a: "As with any AI tool, we recommend that results be validated by a professional. However, our accuracy rate for structured data extraction and peak detection exceeds 98%."
      },
      {
        q: "Can I export charts for my presentations?",
        a: "Absolutely! You can download full PDF reports or export cleaned data to use in your presentations and publications."
      }
    ]
  },
  {
    category: "Pricing & Subscriptions",
    questions: [
      {
        q: "Is there a free plan?",
        a: "Yes! Our free plan includes 5 analyses per month, perfect for trying out TabedLab and occasional use."
      },
      {
        q: "Can I upgrade or downgrade my plan?",
        a: "Yes, you can change your subscription plan at any time. Changes take effect at the start of your next billing cycle."
      },
      {
        q: "What payment methods do you accept?",
        a: "We accept all major credit cards (Visa, MasterCard, American Express) through Stripe, as well as PayPal."
      }
    ]
  }
];

const FAQPage = () => {
  const navigate = useNavigate();
  const [openFAQ, setOpenFAQ] = useState(null);

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <SEO 
        title="FAQ - Frequently Asked Questions"
        description="Find answers to common questions about TabedLab's AI-powered spectral analysis platform. Learn about data security, supported formats, pricing, and more."
        keywords="TabedLab FAQ, spectroscopy questions, AI analysis FAQ, laboratory software help, FTIR FAQ"
        url="/faq"
      />
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-md border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={() => navigate('/')}
          >
            <img 
              src="/tabedlab-logo.png" 
              alt="TabedLab Logo" 
              className="w-10 h-10 object-contain"
            />
            <span className="text-xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
              TabedLab
            </span>
          </div>
          
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="text-slate-300 hover:text-white hover:bg-slate-800"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 mb-4">
            <HelpCircle className="w-4 h-4 text-amber-400" />
            <span className="text-sm text-amber-300">Got Questions?</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            <span className="text-white">Frequently Asked </span>
            <span className="bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">Questions</span>
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Everything you need to know about TabedLab. Can't find what you're looking for? Feel free to contact us.
          </p>
        </div>

        {/* FAQ Categories */}
        <div className="space-y-10">
          {FAQ_DATA.map((category, catIndex) => (
            <div key={catIndex}>
              <h2 className="text-xl font-semibold text-cyan-400 mb-4 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-cyan-400"></div>
                {category.category}
              </h2>
              <div className="space-y-3">
                {category.questions.map((faq, faqIndex) => {
                  const faqId = `${catIndex}-${faqIndex}`;
                  const isOpen = openFAQ === faqId;
                  
                  return (
                    <div 
                      key={faqIndex}
                      className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden hover:border-slate-600 transition-colors"
                    >
                      <button
                        onClick={() => setOpenFAQ(isOpen ? null : faqId)}
                        className="w-full px-6 py-4 flex items-center justify-between text-left"
                      >
                        <span className="font-medium text-white pr-4">{faq.q}</span>
                        <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                      </button>
                      {isOpen && (
                        <div className="px-6 pb-4">
                          <p className="text-slate-400 leading-relaxed">{faq.a}</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Still Have Questions */}
        <div className="mt-16 text-center bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
          <Beaker className="w-12 h-12 mx-auto mb-4 text-cyan-400" />
          <h3 className="text-xl font-semibold text-white mb-2">Still have questions?</h3>
          <p className="text-slate-400 mb-6">
            Can't find the answer you're looking for? Our support team is here to help.
          </p>
          <Button
            onClick={() => navigate('/contact')}
            className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white"
          >
            Contact Us
          </Button>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-800 py-8 px-6">
        <div className="max-w-5xl mx-auto text-center text-slate-500 text-sm">
          <p>© {new Date().getFullYear()} TabedLab. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default FAQPage;
